import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { DayScheduleRow, DayScheduleSummary } from '../services/dayScheduleService';
import { colors } from '../theme/designSystem';
import { formatE164Display } from '../utils/phone';
import type { DayScheduleBlock } from '../clients/DaySchedule';

/**
 * Impressão da Agenda do dia (?page=day-schedule-print): lista corrida das sessões filtradas na tela,
 * com telefone de contato no lugar dos botões, no template padrão da entidade. Os dados chegam pela
 * localStorage gravada pela página (mesmo esquema do extrato financeiro); apagada ao fechar a aba.
 */
export const DAY_SCHEDULE_PRINT_KEY = 'clinic4us-day-schedule-print';

export interface DaySchedulePrintPayload {
  filtersLabel: string;
  rows: DayScheduleRow[];
  /** Resumo dos cards da tela (RN-546a); ausente em impressões abertas antes da mudança */
  summary?: DayScheduleSummary;
  /** Bloqueios de agenda do filtro (RN-118n); vazio quando a tela os ocultou */
  blocks?: DayScheduleBlock[];
}

const fmtDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const fmtTime = (iso: string) => iso.slice(11, 16);

const DaySchedulePrint: React.FC = () => {
  const { user } = useAuth();
  const [payload, setPayload] = useState<DaySchedulePrintPayload>({ filtersLabel: '', rows: [] });
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'day-schedule');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Clinic4Us - Agenda do dia';
    const load = async () => {
      try {
        let stored: DaySchedulePrintPayload = { filtersLabel: '', rows: [] };
        try {
          const raw = localStorage.getItem(DAY_SCHEDULE_PRINT_KEY) ?? sessionStorage.getItem(DAY_SCHEDULE_PRINT_KEY);
          if (raw) stored = JSON.parse(raw);
          // Apaga ao fechar a aba, não na leitura: o StrictMode do dev roda o efeito duas vezes
          window.addEventListener('pagehide', () => localStorage.removeItem(DAY_SCHEDULE_PRINT_KEY), { once: true });
        } catch { /* vazio */ }
        const templates = user?.entityId ? await printTemplateService.getByEntity(user.entityId).catch(() => []) : [];
        setPayload(stored);
        setTemplates(templates);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: colors.primary }} /></Box>;
  }

  const rows = payload.rows;
  const blocks = payload.blocks ?? [];
  const cell: React.CSSProperties = { padding: '1.5mm 1.5mm', borderBottom: '1px solid #ddd', fontSize: '9.5pt', verticalAlign: 'top' };
  const head: React.CSSProperties = { ...cell, fontWeight: 700, background: '#f1f3f5', borderBottom: '1px solid #999' };
  // Sessões e bloqueios na mesma sequência do dia: no papel se lê pelo horário
  type Line = { kind: 'session'; start: string; row: DayScheduleRow } | { kind: 'block'; start: string; block: DayScheduleBlock };
  const lines: Line[] = [
    ...rows.map((r): Line => ({ kind: 'session', start: r.start, row: r })),
    ...blocks.map((b): Line => ({ kind: 'block', start: b.dateTimeInitial, block: b })),
  ];
  const byDay = lines.reduce<Record<string, Line[]>>((acc, l) => { const k = l.start.slice(0, 10); (acc[k] = acc[k] || []).push(l); return acc; }, {});
  Object.values(byDay).forEach((list) => list.sort((a, z) => a.start.localeCompare(z.start)));

  const blockRow = (b: DayScheduleBlock) => (
    <tr key={`b-${b.id}`} style={{ background: '#f8f9fa' }}>
      <td style={cell}>{fmtTime(b.dateTimeInitial)}</td>
      <td style={cell}>{fmtTime(b.dateTimeFinal)}</td>
      <td style={cell} colSpan={2}>
        <div style={{ fontWeight: 600 }}>Bloqueio: {b.title}</div>
        {b.notes && <div style={{ color: '#6c757d', fontSize: '8.5pt' }}>{b.notes}</div>}
      </td>
      <td style={cell}>{b.professionalName || '—'}</td>
      <td style={{ ...cell, color: '#6c757d' }} colSpan={2}>não é atendimento</td>
    </tr>
  );

  const sessionRow = (r: DayScheduleRow) => (
    <tr key={r.scheduleId}>
      <td style={cell}>{fmtTime(r.start)}</td>
      <td style={cell}>{fmtTime(r.end)}</td>
      <td style={cell}>
        <div style={{ fontWeight: 600 }}>{r.patientName || '—'}</div>
        {r.responsibleName && <div style={{ color: '#6c757d', fontSize: '8.5pt' }}>Resp. {r.responsibleName}</div>}
      </td>
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>{formatE164Display(r.phone) ?? '—'}</td>
      <td style={cell}>
        <div>{r.professionalName || '—'}</div>
        {(r.specialty || r.serviceName || r.planLabel) && <div style={{ color: '#6c757d', fontSize: '8.5pt' }}>{[r.specialty, r.serviceName, r.planLabel].filter(Boolean).join(' · ')}</div>}
      </td>
      <td style={cell}>{r.confirmation || '—'}{r.sentCount > 0 ? <div style={{ color: '#6c757d', fontSize: '8.5pt' }}>WhatsApp {r.sentCount}×</div> : null}</td>
      <td style={cell}>{r.presence || '—'}</td>
    </tr>
  );

  return (
    <Box sx={{ background: '#e9ecef', minHeight: '100vh', py: 3, '@media print': { background: 'white', py: 0 } }}>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .ds-print-page { padding: 0 !important; width: auto !important; min-height: 0 !important; box-shadow: none !important; margin: 0 !important; }
          .print-frame-header img { max-height: 18mm !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button variant="contained" startIcon={<Print />} onClick={() => window.print()} sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}>
          Imprimir
        </Button>
      </Box>

      <Box className="ds-print-page" sx={{
        width: '210mm', maxWidth: '100%', minHeight: '297mm', padding: '15mm', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
        background: 'white', margin: '0 auto 2rem', boxShadow: '0 0 8px rgba(0,0,0,0.15)', fontSize: '12pt', color: '#000',
      }}>
        {template && (
<div className="print-frame-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10mm' }}>
            <div style={{ flexShrink: 0 }}>
              {template?.file ? <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} /> : null}
            </div>
            <div style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }} />
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
        </div>
)}

        <Box sx={{ flex: '1 1 auto' }}>
          <Typography sx={{ fontSize: '15pt', fontWeight: 700, color: '#212529', mb: '3mm', letterSpacing: '.03em' }}>AGENDA DO DIA</Typography>
          <Box sx={{ mb: '5mm', fontSize: '10.5pt', color: '#212529', lineHeight: 1.6 }}>
            {payload.filtersLabel && <div><strong>Filtro:</strong> {payload.filtersLabel}</div>}
            {payload.summary && (
              <div>
                <strong>Resumo:</strong> {payload.summary.patients} pacientes, {payload.summary.sessions} atendimentos ·{' '}
                {payload.summary.confirmed} confirmados, {payload.summary.pending} pendentes · {payload.summary.cancelled} cancelados ·{' '}
                {payload.summary.attended} presenças, {payload.summary.noShow} faltas, {payload.summary.withoutPresence} sem registro ·{' '}
                {payload.summary.paid} pagos, {payload.summary.unpaid} não pagos
                <span style={{ color: '#6c757d' }}> (sem pacientes de teste)</span>
              </div>
            )}
            <div><strong>Sessões:</strong> {rows.length}{blocks.length > 0 ? <> · <strong>Bloqueios:</strong> {blocks.length}</> : null} · <strong>Emitido em:</strong> {new Date().toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
          </Box>

          {lines.length === 0 ? (
            <Typography sx={{ color: '#6c757d', py: 4, textAlign: 'center' }}>Nenhuma sessão no filtro escolhido.</Typography>
          ) : Object.keys(byDay).sort().map((day) => (
            <Box key={day} sx={{ mb: '5mm' }}>
              {Object.keys(byDay).length > 1 && <Typography sx={{ fontSize: '11pt', fontWeight: 700, mb: '1.5mm', color: '#212529' }}>{fmtDate(day)}</Typography>}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...head, textAlign: 'left', width: '17mm' }}>Início</th>
                    <th style={{ ...head, textAlign: 'left', width: '13mm' }}>Fim</th>
                    <th style={{ ...head, textAlign: 'left' }}>Paciente / responsável</th>
                    <th style={{ ...head, textAlign: 'left', width: '34mm' }}>Telefone</th>
                    <th style={{ ...head, textAlign: 'left' }}>Terapeuta / especialidade</th>
                    <th style={{ ...head, textAlign: 'left', width: '24mm' }}>Confirmação</th>
                    <th style={{ ...head, textAlign: 'left', width: '22mm' }}>Presença</th>
                  </tr>
                </thead>
                <tbody>
                  {byDay[day].map((line) => (line.kind === 'block' ? blockRow(line.block) : sessionRow(line.row)))}
                </tbody>
              </table>
            </Box>
          ))}
        </Box>

        {template && (
<div style={{ marginTop: 'auto', textAlign: 'center', fontSize: '9pt', paddingTop: '6mm', borderTop: '1px solid #ddd' }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }} />
)}
      </Box>
    </Box>
  );
};

export default DaySchedulePrint;
